import { moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AdminService } from '../../../admin/admin.service';
import { DataToolsApps } from '../../../shared/DataToolsApps';
import { UtilsService } from '../../../shared/services/utils.service';
import { AllPrimaryEntitiesNames, DataRuleId } from '../../data-rules-model';

class RawChainData {
  id: DataRuleId;
  name: string;
  ids: string[];

  constructor(
    id: DataRuleId,
    name: string,
    ids: string[]
  ) {
    this.id = id;
    this.name = name;
    this.ids = ids;
  }

}

export class ChainData {
  id: DataRuleId;
  name: string;
  ruleIds: string[];

  constructor(
    id: DataRuleId,
    name?: string,
    ruleIds?: string[]
  ) {
    this.id = id;
    this.name = name;
    this.ruleIds = ruleIds;
  }
}

export class ComplexChainData {
  id: DataRuleId;
  name: string;
  rules: ChainRuleData[];

  constructor(
    id: DataRuleId,
    name?: string,
    rules?: ChainRuleData[]
  ) {
    this.id = id;
    this.name = name;
    this.rules = rules;
  }
}

export class ChainRuleData {
  id: DataRuleId;
  name?: string;
  entity?: AllPrimaryEntitiesNames;

  constructor(
    id: DataRuleId,
    name?: string,
    entity?: AllPrimaryEntitiesNames
  ) {
    this.id = id;
    this.name = name;
    this.entity = entity;
  }

}

export interface IRuleInChainData {
  id: string;
  chainId: DataRuleId;
  index: number;
}

export abstract class AbstractRuleChainsDataService {

  protected constructor(
    private utilsService: UtilsService
  ) {
  }

  // use at every place where this._chains is used to prevent cases of array-like interacting with null
  public get isInitialisedOnce(): boolean {
    return typeof this._chains !== 'undefined';
  }

  public get loading(): boolean {
    return this._loading === true;
  }

  // use at every place where this._chains is used to prevent cases of array-like interacting with null
  private get isNoChains(): boolean {
    return this._chains === null;
  }

  public get allChains(): ChainData[] {
    // not great sorting here as it gets hit a lot - isolate the chains list changes and sort there (add and maybe update?)
    const noChains = this.isNoChains || !this.isInitialisedOnce;
    if (noChains)
      return [];
    this._chains.sort((a: ChainData, b: ChainData) => this.sortChains(a, b));
    return [...this._chains];
  }

  private sortChains(a: ChainData, b: ChainData) {
    return a.name.toUpperCase().localeCompare(b.name.toUpperCase()) as -1 | 1 | 0;
  }

  public get allSavedChains(): ChainData[] {
    return this.allChains.filter(chain => !this.utilsService.compareInternalDataRuleIds(chain.id, 0));
  }

  // Originally _chains is undefined and ll never be that then, once it changed to anything of the specified types
  // DO NOT MANUALLY SET THAT PROPERTY TO UNDEFINED ANYWHERE
  private _chains: ChainData[] | null;

  private _loading = true;

  // That emitter made for purpose of consumers that requires to poll usedRules info very frequently, like html templating data
  public usedRules = new EventEmitter<IRuleInChainData[]>();

  public utility = class ChainUtility {
    static mapRawChainDataToChain = (rawChain: RawChainData): ChainData => {
      const {id, name, ids} = rawChain;
      return new ChainData(id, name, ids);
    }
  };

  protected abstract loadAllChains(): Observable<RawChainData[]>;

  protected abstract loadChainById(id: string): Observable<RawChainData>;

  protected abstract saveChainAction(data: RawChainData): Observable<RawChainData>;

  protected abstract deleteChainAction(id: string): Observable<void>;

  private emitUsedRules(): void {
    this.getRulesThatInChainsData().subscribe(rules => {
        this.usedRules.emit(rules);
      }
    );
  }

  public getChain(chainId: DataRuleId): ChainData {
    return this.isNoChains ? null : this._chains.find(chain => this.utilsService.compareInternalDataRuleIds(chainId, chain.id));
  }

  public getChainByRuleId(ruleId: DataRuleId): ChainData {
    return this.isNoChains ? null
      : this._chains
        .find(chain => chain.ruleIds
        .findIndex(chainedRuleId => this.utilsService.compareInternalDataRuleIds(chainedRuleId, ruleId)) > -1);
  }

  public getChainIndex(id: DataRuleId): number {
    return this.allChains.findIndex(chain => this.utilsService.compareInternalDataRuleIds(chain.id, id));
  }

  initialize(force: boolean): Observable<void> {
    // do load of chains if nothing in the internal data
    if (
      !force && this.isInitialisedOnce
    ) {
      return of(undefined);
    }

    if (!force && this.loading) {
      // TODO Surely not the best way to wait happening loadings, any better decisions are welcome
      let waiter = setTimeout(() => {
        if (!this.loading) {
          waiter = undefined;
          return of(undefined);
        }
      }, 200);
    } else {
      // that flag means only "force" or first-time initialize process
      this._loading = true;

      return this.loadAllChains().pipe(
        map(rawChains => rawChains.map(this.utility.mapRawChainDataToChain)),
        map(chains => {
          chains.length > 0 ?
            this._chains = chains
            : this._chains = null;
          this.emitUsedRules();
          this._loading = false;
        })
      );
    }
  }

  public addRule(chainId: DataRuleId, ruleId: string): void {
    if (this.isNoChains)
      return;

    this._chains.forEach((chain, i) => {
      if (this.utilsService.compareInternalDataRuleIds(chain.id, chainId)) {
        this._chains[i].ruleIds.push(ruleId);
      }
    });
  }

  removeRuleFromChain(chainId: DataRuleId, ruleIndex: number): void {
    if (this.isNoChains)
      return;

    this._chains.forEach(chain => {
      if (this.utilsService.compareInternalDataRuleIds(chainId, chain.id)) {
        chain.ruleIds.splice(ruleIndex, 1);
        this.emitUsedRules();
      }
    });
  }

  public moveRule(chainId: DataRuleId, prevIndex: number, newIndex: number): void {
    const chain = this.allChains.find(ch => this.utilsService.compareInternalDataRuleIds(ch.id, chainId));
    moveItemInArray(chain.ruleIds, prevIndex, newIndex);
    this.emitUsedRules();
  }

  public ruleUpdated(ruleId: string): Observable<void> {
    if (this.isNoChains)
      return of(undefined);

    return new Observable<void>(obs => {
      const [chainIndexToUpdate, updatedRuleIndex] = this.allChains.reduce(
        (result, chain, chainIndex) => {
          const ruleIndex = chain.ruleIds.findIndex(ruleIdToFind => this.utilsService.compareInternalDataRuleIds(ruleIdToFind, ruleId));
          if (ruleIndex > -1) {
            return [chainIndex, ruleIndex];
          }
          return result;
        }, [-1, -1]);

      if (chainIndexToUpdate > -1 && updatedRuleIndex > -1) {
          this._chains[chainIndexToUpdate].ruleIds[updatedRuleIndex] = ruleId;
          obs.next();
          obs.complete();
      } else {
        obs.next();
        obs.complete();
      }
    });
  }

  public getRulesThatInChainsData(): Observable<IRuleInChainData[]> {
    return this.initialize(false).pipe(
      map(_ => {
        const result = this.allChains.reduce<IRuleInChainData[]>((accum, chain) => {
          chain.ruleIds.forEach((ruleId, i) => {
            accum.push({
              id: ruleId,
              chainId: chain.id,
              index: i
            });
          });
          return accum;
        }, []);
        return result;
      })
    );
  }

  public createChain(data: ChainData): void {
    if (this.isNoChains) {
      this._chains = [data];
      return;
    }
    this._chains.push(data);
  }

  public saveChain(data: ChainData): Observable<string> {
    if (this.isNoChains) {
      return of(undefined);
    }
    return this.saveChainAction(new RawChainData(data.id, data.name, data.ruleIds)).pipe(
      map(this.utility.mapRawChainDataToChain),
      map((updatedChainData) => {
        const updatedChainIndex = this.getChainIndex(data.id);
        this._chains[updatedChainIndex] = updatedChainData;
        this.emitUsedRules();
        return updatedChainData.id as string;
      }),
    );
  }

  public deleteChain(id: DataRuleId): Observable<void> {
    if (this.isNoChains) {
      return of(undefined);
    }
    const chainIndex = this.getChainIndex(id);
    const chainToDelete = this.allChains[chainIndex];
    console.table(chainToDelete);

    if (chainIndex === -1) {
      // TODO Handle the error messages
      return of(undefined);
    }

    const source = this.utilsService.compareInternalDataRuleIds(id, 0) ? of(undefined) : this.deleteChainAction(id as string);

    return source.pipe(
      map(() => {
        this._chains.splice(chainIndex, 1);
        this.emitUsedRules();
      })
    );
  }

  public restoreChain(id: DataRuleId): Observable<void> {
    if (this.utilsService.compareInternalDataRuleIds(id, 0)) {
      return of(undefined);
    }

    return this.loadChainById(id as string).pipe(
      map(this.utility.mapRawChainDataToChain),
      map(chainData => {
        const index = this.getChainIndex(id);
        this._chains[index] = chainData;
      })
    );
  }
}

@Injectable({
  providedIn: 'root'
})
export class RuleChainsDataService extends AbstractRuleChainsDataService {

  public isEnabled: boolean;

  constructor(private http: HttpClient,
              private adminService: AdminService,
              utilsService: UtilsService) {
    super(utilsService);
    this.updateEnabledStatus();
  }

  public updateEnabledStatus(): void {
    this.isEnabled = this.adminService.checkAppEnabled(DataToolsApps.DATARULES_CHAINED_RULES);
  }

  // for that moment Middleware handles class methods with only one argument
  private appEnabledMiddleware<A, R>(notEnabledDefault: R, arg: A, func: (x: A) => R): R {
    this.updateEnabledStatus();
    if (!this.isEnabled)
      return notEnabledDefault;

    return func(arg);
  }

  protected loadAllChains(): Observable<RawChainData[]> {
    return this.appEnabledMiddleware<void, Observable<RawChainData[]>>(
      of([]),
      undefined,
      () => {
        console.time('loadAllChains');
        return this.http.get<RawChainData[]>(`${environment.dataRulesUrl}/chain`).pipe(
          tap(() => console.timeEnd('loadAllChains')),
          tap(allChains => console.log('KPDR: Chains', allChains.length))
        );
      }
    );
  }

  protected loadChainById(id: string): Observable<RawChainData> {
    return this.appEnabledMiddleware<string, Observable<RawChainData>>(
      of(undefined),
      id,
      (idArg) => {
        return this.http.get<RawChainData>(`${environment.dataRulesUrl}/chain/${idArg}`).pipe(
          tap(chain => console.log('KPDR: Chain', chain))
        );
      }
    );
  }

  protected saveChainAction(chain: RawChainData): Observable<RawChainData> {
    return this.appEnabledMiddleware<RawChainData, Observable<RawChainData>>(
      of(undefined),
      chain,
      (chainArg) => {
        let httpResponse: Observable<RawChainData>;

        if (chainArg.id) {
          httpResponse = this.http.put<RawChainData>(`${environment.dataRulesUrl}/chain/${chainArg.id}`, chainArg);
        } else {
          httpResponse = this.http.post<RawChainData>(`${environment.dataRulesUrl}/chain/`, chainArg);
        }

        return httpResponse;
      }
    );
  }

  protected deleteChainAction(id: string): Observable<void> {
    return this.appEnabledMiddleware<string, Observable<void>>(
      of(undefined),
      id,
      (idArg) => {
        return this.http.delete<void>(`${environment.dataRulesUrl}/chain/${idArg}`);
      }
    );
  }

}
